import { useEffect, useCallback, useReducer } from 'react';
import axios from 'axios';
import shuffle from 'lodash/shuffle';
import { BannerData, Offer } from '@wix/dealer-common/dist/src/interfaces';
import { topNavOffersFixture } from './offersFixture';

// Set the fixture to test offer paths locally without fetching from Dealer
const OFFERS_FIXTURE: null | 'withModal' | 'banner' = null;

export function sendDealerCommand(
  command: string,
  realEstateId: string,
  offerName: string = '',
  data?: any
) {
  if (window.parent.postMessage) {
    const payload = {
      type: 'dealerAd',
      command,
      realEstateId,
      offerName,
      data,
    };
    window.parent.postMessage(payload, '*');
  }
}

interface State {
  isEmpty: boolean;
  isLoaded: boolean;
  banners: BannerData[];
  offers: Offer[];
}

interface Action {
  type: string;
  banners?: BannerData[];
  bannerId?: string;
  offers?: Offer[];
  offerId?: string;
}

function dealerAdReducer(
  state: State,
  { type, banners = [], bannerId, offers = [], offerId }: Action
) {
  switch (type) {
    case 'empty':
      return state.isEmpty
        ? state
        : {
            isLoaded: true,
            banners: [],
            isEmpty: true,
            offers: [],
          };
    case 'load':
      return state.isLoaded
        ? state
        : {
            isLoaded: true,
            banners,
            isEmpty: banners.length === 0 && offers.length === 0,
            offers,
          };
    case 'close':
      const filteredBanners = state.banners.filter(
        b => b.bannerId !== bannerId
      );
      return bannerId && filteredBanners.length !== state.banners.length
        ? {
            ...state,
            banners: filteredBanners,
            isEmpty: filteredBanners.length > 0,
          }
        : state;
    case 'closeOffer':
      const filteredOffers = state.offers.filter(b => b.offerGuid !== offerId);
      return offerId && filteredOffers.length !== state.offers.length
        ? {
            ...state,
            offers: filteredOffers,
            isEmpty: filteredOffers.length > 0,
          }
        : state;
    default:
      return state;
  }
}

export function useDealerBanners(
  realEstateId: string,
  biClientIdSigned: string,
  offerName: string = '',
  userGuid = ''
) {
  const [{ isLoaded, banners, isEmpty, offers }, dispatch] = useReducer(
    dealerAdReducer,
    {
      isLoaded: false,
      isEmpty: false,
      banners: [],
      offers: [],
    }
  );

  const onEmpty = useCallback(() => {
    dispatch({ type: 'empty' });
    sendDealerCommand('ad-empty', realEstateId, offerName);
  }, [realEstateId]);

  const closeOffer = useCallback((offerToClose: Offer) => {
    dispatch({ type: 'closeOffer', offerId: offerToClose.offerGuid });
  }, []);

  const loadOffers = useCallback((newOffers: Offer[]) => {
    dispatch({ type: 'load', offers: newOffers });

    // We only ever render the first offer, so notify that one
    if (newOffers.length) {
      const firstOffer = newOffers[0];
      axios.create().post(
        `https://apps.wix.com/_api/dealer-offer-events-service/v1/dealer-offer-events/${firstOffer.offerGuid}`,
        { event: {
          type: 'VIEW',
          realEstateId,
          userId: userGuid,
          targetAccountId: userGuid,
          loggedInAccountId: userGuid,
        }}
      );
    }
  }, [userGuid, realEstateId]);

  const onCtaClick = useCallback((offerClicked: Offer) => {
    if (offerClicked) {
      axios.create().post(
        `https://apps.wix.com/_api/dealer-offer-events-service/v1/dealer-offer-events/${offerClicked.offerGuid}`,
        { event: {
          type: 'MAIN_CTA_CLICK',
          realEstateId,
          userId: userGuid,
          targetAccountId: userGuid,
          loggedInAccountId: userGuid,
        }}
      );
    }
  }, [userGuid, realEstateId]);

  useEffect(() => {
    if (isLoaded || !realEstateId) {
      return;
    }

    if (!biClientIdSigned) {
      onEmpty();
      return;
    }

    // Validate JWT
    const parts = biClientIdSigned.split('.');
    const header = atob(parts[0] || '');
    if (!/JWT/i.test(header)) {
      onEmpty();
      return;
    }

    const params: any = {
      realEstateId: realEstateId,
      deviantArtUserId: biClientIdSigned,
    };

    const request = OFFERS_FIXTURE
      ? Promise.resolve({
          data: topNavOffersFixture[OFFERS_FIXTURE],
        })
      : axios
          .create(/* do not use wix axios config as it messes up CORS OPTIONS request for preflight */)
          .get(
            'https://apps.wix.com/_api/dealer-offers-serving-service/proxy/v1/dealer/serving/offers',
            {
              headers: {},
              params,
            }
          );

    request
      .then(response => {
        const offersResponse = response?.data?.offers;
        if (offersResponse && offersResponse.length > 0) {
          loadOffers(shuffle(offersResponse));
          sendDealerCommand('ad-filled', realEstateId, offerName);
        } else {
          onEmpty();
        }
      })
      .catch(onEmpty);
  }, [loadOffers, onEmpty, realEstateId, userGuid, isLoaded, biClientIdSigned]);

  if (!realEstateId) {
    onEmpty();
  }

  return {
    onEmpty,
    closeOffer,
    onCtaClick,
    isLoaded,
    banners,
    isEmpty,
    offers,
  };
}
